/**
 * Matcher puro: URL normalizada + LinkTargetMatch → matched + pathParams.
 *
 * Política de trailing slash: paths (exceto `/`) são comparados sem `/` final.
 * Hash é ignorado. Grupos configurados usam AND; arrays internos usam OR.
 */

import type {
  LinkQueryCondition,
  LinkTargetMatch,
} from '@/config/shared/auto-source';

export type LinkTargetMatchedPath = {
  readonly type: 'template' | 'exact' | 'prefix';
  /** Pattern da configuração (nunca o pathname real do usuário). */
  readonly pattern: string;
};

export type LinkTargetMatchResult = {
  readonly matched: boolean;
  readonly pathParams: Readonly<Record<string, string>>;
  readonly matchedPath?: LinkTargetMatchedPath;
};

const EMPTY_PARAMS: Readonly<Record<string, string>> = Object.freeze({});

/**
 * Remove trailing slash (exceto root `/`).
 */
export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const withLeading = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeading.length > 1 && withLeading.endsWith('/')
    ? withLeading.slice(0, -1)
    : withLeading;
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * `/pri` → `/pri6670988` (dígito a seguir), mas não `/privacy`.
 * `/offer_` → `/offer_1` (sufixo não alfanumérico no prefixo).
 */
export function matchesSegmentPrefix(segment: string, prefix: string): boolean {
  const normalizedPrefix = prefix.replace(/^\/+|\/+$/g, '');
  if (!normalizedPrefix) return false;
  if (segment === normalizedPrefix) return true;
  if (!segment.startsWith(normalizedPrefix)) return false;
  if (/[^a-zA-Z0-9]$/.test(normalizedPrefix)) return true;
  const next = segment.charAt(normalizedPrefix.length);
  // `/pri` → `/pri6670988` ou `/pri_123`, mas não `/privacy`.
  if (next >= '0' && next <= '9') return true;
  if (next === '_' && /\d/.test(segment.slice(normalizedPrefix.length + 1))) {
    return true;
  }
  return false;
}

/**
 * Faz match de template simples `/pay/:offerId`.
 * Retorna null se não corresponder; {} se corresponder sem params.
 */
export function matchPathTemplate(
  pathname: string,
  template: string
): Record<string, string> | null {
  const pathParts = normalizePathname(pathname)
    .split('/')
    .filter(Boolean)
    .map(safeDecode);
  const templateParts = normalizePathname(template).split('/').filter(Boolean);

  if (pathParts.length !== templateParts.length) {
    return null;
  }

  const params: Record<string, string> = Object.create(null);
  const seen = new Set<string>();

  for (let i = 0; i < templateParts.length; i += 1) {
    const tpl = templateParts[i]!;
    const seg = pathParts[i]!;
    if (tpl.startsWith(':')) {
      const key = tpl.slice(1);
      if (!key || seen.has(key)) {
        return null;
      }
      // Nomes inseguros / inválidos não capturam (defesa em runtime).
      if (
        key === '__proto__' ||
        key === 'constructor' ||
        key === 'prototype' ||
        !/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)
      ) {
        return null;
      }
      seen.add(key);
      params[key] = seg;
      continue;
    }
    if (tpl !== seg) {
      return null;
    }
  }

  return params;
}

function compareStrings(
  left: string,
  right: string,
  caseSensitive: boolean
): boolean {
  if (caseSensitive) return left === right;
  return left.toLowerCase() === right.toLowerCase();
}

function startsWithSafe(
  value: string,
  prefix: string,
  caseSensitive: boolean
): boolean {
  if (caseSensitive) return value.startsWith(prefix);
  return value.toLowerCase().startsWith(prefix.toLowerCase());
}

function matchQueryCondition(
  params: URLSearchParams,
  condition: LinkQueryCondition
): boolean {
  const raw = params.get(condition.key);
  const caseSensitive =
    'caseSensitive' in condition && condition.caseSensitive === true;

  switch (condition.operator) {
    case 'exists':
      return raw !== null && raw !== '';
    case 'absent':
      return raw === null || raw === '';
    case 'equals':
      if (raw === null) return false;
      return compareStrings(raw, condition.value, caseSensitive);
    case 'starts_with':
      if (raw === null) return false;
      return startsWithSafe(raw, condition.value, caseSensitive);
    case 'one_of': {
      if (raw === null) return false;
      return condition.value.some(v => compareStrings(raw, v, caseSensitive));
    }
    default:
      return false;
  }
}

/**
 * Avalia match do destino. Determinístico e sem side effects.
 */
export function matchLinkTarget(
  url: URL,
  rule: LinkTargetMatch
): LinkTargetMatchResult {
  if (rule.protocols?.length) {
    if (!rule.protocols.includes(url.protocol as 'http:' | 'https:')) {
      return { matched: false, pathParams: EMPTY_PARAMS };
    }
  } else if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { matched: false, pathParams: EMPTY_PARAMS };
  }

  if (rule.origins?.length) {
    const originOk = rule.origins.some(origin => origin === url.origin);
    if (!originOk) {
      return { matched: false, pathParams: EMPTY_PARAMS };
    }
  }

  if (rule.hostnames?.length) {
    const host = url.hostname.toLowerCase();
    const hostOk = rule.hostnames.some(h => h.toLowerCase() === host);
    if (!hostOk) {
      return { matched: false, pathParams: EMPTY_PARAMS };
    }
  }

  let pathParams: Record<string, string> = Object.create(null);
  let matchedPath: LinkTargetMatchedPath | undefined;
  const pathRule = rule.path;
  if (pathRule) {
    const pathname = normalizePathname(url.pathname);

    // Prioriza template para capturar pathParams mesmo com prefixes/exact.
    if (pathRule.template) {
      const params = matchPathTemplate(pathname, pathRule.template);
      if (params) {
        pathParams = params;
        matchedPath = {
          type: 'template',
          pattern: normalizePathname(pathRule.template),
        };
      }
    }

    if (!matchedPath && pathRule.exact?.length) {
      const exactHit = pathRule.exact.find(
        exact => normalizePathname(exact) === pathname
      );
      if (exactHit !== undefined) {
        matchedPath = {
          type: 'exact',
          pattern: normalizePathname(exactHit),
        };
      }
    }

    if (!matchedPath && pathRule.prefixes?.length) {
      const prefixHit = pathRule.prefixes.find(prefix => {
        const normalizedPrefix = normalizePathname(prefix);
        if (normalizedPrefix === '/') return true;
        if (pathname === normalizedPrefix) return true;
        // Directory semantics: `/pay` → `/pay/foo`
        if (pathname.startsWith(`${normalizedPrefix}/`)) return true;
        // Literal pathname prefix when the pattern ends with a separator
        // (e.g. `/offer_` → `/offer_1`). Does not make `/pay` match `/payment`.
        if (
          /[^a-zA-Z0-9/]$/.test(normalizedPrefix) &&
          pathname.startsWith(normalizedPrefix)
        ) {
          return true;
        }
        // Alphanumeric prefix + digit: `/pri` → `/pri6670988`, not `/privacy`.
        const bare = normalizedPrefix.replace(/^\//, '');
        const firstSeg = pathname.split('/').filter(Boolean)[0] ?? '';
        if (
          pathname.startsWith(normalizedPrefix) &&
          matchesSegmentPrefix(firstSeg, bare)
        ) {
          return true;
        }
        return false;
      });
      if (prefixHit !== undefined) {
        matchedPath = {
          type: 'prefix',
          pattern: normalizePathname(prefixHit),
        };
      }
    }

    if (!matchedPath && pathRule.segmentPrefixes?.length) {
      const segments = pathname.split('/').filter(Boolean);
      const segmentHit = pathRule.segmentPrefixes.find(prefix =>
        segments.some(seg => matchesSegmentPrefix(seg, prefix))
      );
      if (segmentHit !== undefined) {
        matchedPath = {
          type: 'prefix',
          pattern: `/${segmentHit.replace(/^\/+|\/+$/g, '')}`,
        };
      }
    }

    if (!matchedPath) {
      return { matched: false, pathParams: EMPTY_PARAMS };
    }
  }

  if (rule.query?.conditions?.length) {
    const mode = rule.query.mode ?? 'all';
    const results = rule.query.conditions.map(c =>
      matchQueryCondition(url.searchParams, c)
    );
    const queryOk =
      mode === 'any' ? results.some(Boolean) : results.every(Boolean);
    if (!queryOk) {
      return { matched: false, pathParams: EMPTY_PARAMS };
    }
  }

  return {
    matched: true,
    pathParams: Object.freeze({ ...pathParams }),
    ...(matchedPath ? { matchedPath } : {}),
  };
}

/**
 * Path seguro para diagnostics: usa a regra configurada, nunca o pathname do usuário.
 * - template → pattern com placeholders (ex.: `/pay/:offerId`)
 * - prefix → `pattern/:redacted`
 * - exact → todos os segmentos redigidos (tokens opacos na config não vazam)
 */
export function formatMatchedPathForDiagnostics(
  matchedPath: LinkTargetMatchedPath | undefined
): string | null {
  if (!matchedPath) return null;
  if (matchedPath.type === 'prefix') {
    const prefix = normalizePathname(matchedPath.pattern);
    return prefix === '/' ? '/:redacted' : `${prefix}/:redacted`;
  }
  if (matchedPath.type === 'exact') {
    const parts = normalizePathname(matchedPath.pattern)
      .split('/')
      .filter(Boolean);
    if (parts.length === 0) return '/';
    return `/${parts.map(() => ':redacted').join('/')}`;
  }
  return matchedPath.pattern;
}
